/**
 * Vuex plugin: Order Form PO leak detector.
 *
 * Instrumentation safety-net for the V2 Order Form autosave bug family.
 *
 * The bug: a facility user had generated a PO (visible in the form and persisted
 * on the purchase_orders row) and submitted the order only ~1m 49s later — yet
 * the order row came through with `purchase_order_id = NULL`. Static analysis
 * of the autosave flow found several paths that could null the in-memory PO
 * (loadOrder wiping it on round-trips, onSaveOrder default, route hook leaks).
 * Those were fixed in Tasks 1-3. This plugin is a permanent tripwire — if any
 * other path nulls `state.shared.orderFormOrder.order.purchaseOrderId` while
 * a user has a PO in-flight, we want a report in Sentry with enough context
 * to identify the mutation responsible.
 *
 * Design:
 *   - Subscribe to mutations. Read the previous and current PO each time.
 *   - Flag non-null/non-empty -> null/empty transitions.
 *   - Suppress the user-initiated Clear button (explicit updateOrder with
 *     attribute='purchaseOrderId' and value=null — see `clearCustomPurchaseOrder`
 *     in use/orderFormOrder.js).
 *   - Suppress the post-submit reset: setOrder with a fully-blank order is the
 *     expected clean slate after a successful submit.
 *   - Everything else is reported via Sentry.captureMessage (falls back to
 *     console.warn if Sentry isn't available, e.g. in unit tests).
 *
 * Keep this plugin intentionally small. It is instrumentation, not business
 * logic. When these PO-null bugs stop reporting for a meaningful window, the
 * plugin can be removed.
 */

import * as Sentry from '@sentry/vue';

const PO_PATH = 'shared/orderFormOrder';

function hasValue(v) {
    return v !== null && v !== undefined && v !== '';
}

/**
 * Heuristic: is this setOrder payload a fully-blank order?
 *
 * `createBlankOrder` commits setOrder with the `blankOrder()` getter output —
 * id is null, purchaseOrderId is null, isDirty is false. We intentionally do
 * NOT check items.length so that future changes to the blank-order shape
 * (e.g., starting with 0 or 2 blank items) don't silently turn every
 * post-submit reset into a false-positive Sentry alert.
 */
function looksLikeBlankOrder(order) {
    if (!order || typeof order !== 'object') {
        return false;
    }
    return (
        order.id === null &&
        order.purchaseOrderId === null &&
        order.isDirty === false &&
        Array.isArray(order.items)
    );
}

function currentRouteContext() {
    try {
        const router = typeof window !== 'undefined' ? window?.app?.$router : null;
        if (router && router.currentRoute) {
            const route = router.currentRoute;
            return {
                routeName: route.name || null,
                routeFullPath: route.fullPath || null,
                routeQuery: route.query || null,
            };
        }
    } catch (_) {
        // fall through
    }
    if (typeof window !== 'undefined' && window.location) {
        return {
            routeName: null,
            routeFullPath: window.location.pathname + window.location.search,
            routeQuery: null,
        };
    }
    return { routeName: null, routeFullPath: null, routeQuery: null };
}

function report(payload) {
    try {
        if (Sentry && typeof Sentry.captureMessage === 'function') {
            Sentry.captureMessage('[OrderForm:PO-leak] unexpected PO null transition', {
                level: 'warning',
                extra: payload,
            });
            return;
        }
    } catch (_) {
        // fall through to console
    }
    // eslint-disable-next-line no-console
    console.warn('[OrderForm:PO-leak] unexpected PO null transition', payload);
}

export default function orderFormPoLeakDetector(store) {
    let previousPo = null;
    try {
        previousPo = store?.state?.shared?.orderFormOrder?.order?.purchaseOrderId ?? null;
    } catch (_) {
        previousPo = null;
    }

    store.subscribe((mutation, state) => {
        // Only care about mutations inside the orderFormOrder namespace.
        if (!mutation || typeof mutation.type !== 'string' || !mutation.type.startsWith(PO_PATH + '/')) {
            // But the PO value itself could still change indirectly — re-sync our
            // cached previousPo anyway so we don't false-alarm on the next mutation.
            const current = state?.shared?.orderFormOrder?.order?.purchaseOrderId;
            previousPo = current === undefined ? null : current;
            return;
        }

        const current = state?.shared?.orderFormOrder?.order?.purchaseOrderId;
        const currentNormalized = current === undefined ? null : current;

        const wasSet = hasValue(previousPo);
        const isCleared = !hasValue(currentNormalized);

        if (wasSet && isCleared) {
            const isExplicitClear =
                mutation.type === `${PO_PATH}/updateOrder` &&
                mutation.payload &&
                mutation.payload.attribute === 'purchaseOrderId' &&
                (mutation.payload.value === null || mutation.payload.value === '');

            const isPostSubmitReset =
                mutation.type === `${PO_PATH}/setOrder` &&
                mutation.payload &&
                looksLikeBlankOrder(mutation.payload.order);

            if (!isExplicitClear && !isPostSubmitReset) {
                const route = currentRouteContext();
                const stack = new Error('PO transition trace').stack;
                report({
                    previousPurchaseOrderId: previousPo,
                    currentPurchaseOrderId: currentNormalized,
                    mutationType: mutation.type,
                    mutationPayloadShape: describePayload(mutation.payload),
                    ...route,
                    stack,
                });
            }
        }

        previousPo = currentNormalized;
    });
}

/**
 * Produce a small, Sentry-safe summary of the mutation payload. We deliberately
 * do NOT ship the whole payload (it can include PII line items, patient names,
 * etc.). Keys + types is enough to identify which code path triggered.
 */
function describePayload(payload) {
    if (payload === null || payload === undefined) {
        return String(payload);
    }
    if (typeof payload !== 'object') {
        return typeof payload;
    }
    const summary = {};
    Object.keys(payload).forEach((k) => {
        const v = payload[k];
        if (v === null) {
            summary[k] = 'null';
        } else if (Array.isArray(v)) {
            summary[k] = `array(${v.length})`;
        } else {
            summary[k] = typeof v;
        }
    });
    return summary;
}
